import { useState, useEffect } from "react";
import { useSEO } from "@/hooks/use-seo";
import Footer from "@/components/Footer";
import { Link, useLocation } from "wouter";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ArrowRight, ArrowLeft, Mail, MapPin, Phone, Send, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import { motion } from "framer-motion";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { gaEvent } from "@/lib/analytics";
import robinImg from "@assets/1678270794100_1771852265016.jpeg";
import logoWrkt from "@assets/Wrkt_-_PNG_1771840403931.png";
import { HeroBackground } from "@/components/HeroBackground";

const SHOW_BOOKING = false; // backlog: aanzetten zodra Cal.com-account bestaat
const CAL_LINK_VIDEO = "wrkt/kennismaking-videocall";
const CAL_LINK_OFFICE = "wrkt/kennismaking-amsterdam";

export default function Contact() {
  const { t, language, toggleLanguage, localePath } = useLanguage();
  const [location, setLoc] = useLocation();
  const { toast } = useToast();
  const [isSuccess, setIsSuccess] = useState(
    window.location.pathname.endsWith('/contact/succes') || window.location.pathname.endsWith('/contact/success')
  );
  const [scrolled, setScrolled] = useState(false);
  const [meetingType, setMeetingType] = useState<'video' | 'office'>('video');

  const nl = language === 'nl';
  useSEO({
    title: nl
      ? 'Contact | Wrkt Digital - Digital Marketing Bureau Amsterdam'
      : 'Contact | Wrkt Digital - Digital Marketing Agency Amsterdam',
    description: nl
      ? 'Plan direct een kennismaking met Wrkt Digital, digital marketing bureau in Amsterdam. Je spreekt meteen Robin de Wildt. Vrijblijvend gesprek over jouw D2C e-commerce groei.'
      : 'Book an intro call with Wrkt Digital, digital marketing agency in Amsterdam. You speak directly with Robin de Wildt. No-obligation conversation about your D2C e-commerce growth.',
    path: '/contact',
    lang: nl ? 'nl' : 'en',
    keywords: nl
      ? 'contact digital marketing bureau, marketing bureau amsterdam contact, Wrkt Digital contact, vrijblijvend marketing gesprek, growth review aanvragen, D2C marketing advies, e-commerce marketing bureau contact, Robin de Wildt'
      : 'contact digital marketing agency, marketing agency amsterdam contact, Wrkt Digital contact, free marketing consultation, growth review request, D2C marketing advice, e-commerce marketing agency contact, Robin de Wildt',
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    source: "",
    message: "",
  });

  const getHubSpotCookie = () => {
    const name = "hubspotutk=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') c = c.substring(1);
      if (c.indexOf(name) == 0) return c.substring(name.length, c.length);
    }
    return undefined;
  };

  const submitToHubSpot = (data: typeof formData) => {
    const hutk = getHubSpotCookie();
    const sourceLabels: Record<string, string> = {
      google: 'Google',
      social: 'Social Media',
      referral: 'Referral / mond-tot-mond',
      linkedin: 'LinkedIn',
      other: 'Anders',
    };
    const messageWithSource = `Bron: ${sourceLabels[data.source] || data.source}\n\n${data.message}`;
    try {
      fetch('https://api.hsforms.com/submissions/v3/integration/submit/612516/04b1a42f-8aa1-4642-8b53-5c49d6919443', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields: [
            { name: 'email', value: data.email },
            { name: 'firstname', value: data.name },
            { name: 'company', value: data.company },
            { name: 'message', value: messageWithSource },
          ],
          context: {
            pageUri: window.location.href,
            pageName: "WRKT Contact Formulier",
            ...(hutk ? { hutk } : {}),
          },
        }),
      }).catch(err => console.error("HubSpot submission error:", err));
    } catch (e) { console.error(e); }
  };

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await apiRequest("POST", "/api/contacts", data);
      return res.json();
    },
    onSuccess: (_result, submittedData) => {
      submitToHubSpot(submittedData);
      if (typeof window !== 'undefined' && (window as any).fbq) {
        (window as any).fbq('track', 'Lead', { content_name: 'Contact Form' });
      }
      gaEvent('contact_submit', { language });
      setIsSuccess(true);
      setLoc(localePath(language === 'en' ? '/contact/success' : '/contact/succes'));
      setFormData({ name: "", email: "", company: "", source: "", message: "" });
      toast({
        title: t("contact_success_title"),
        description: t("contact_success_desc"),
        className: "bg-[#4CEAAC]/10 border-[#4CEAAC]/20 text-white",
      });
    },
    onError: () => {
      toast({
        title: language === "nl" ? "Er ging iets mis" : "Something went wrong",
        description: language === "nl" ? "Probeer het later opnieuw" : "Please try again later",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  const steps = [
    { title: t("contact_step1_t"), desc: t("contact_step1_d") },
    { title: t("contact_step2_t"), desc: t("contact_step2_d") },
    { title: t("contact_step3_t"), desc: t("contact_step3_d") },
    { title: t("contact_step4_t"), desc: t("contact_step4_d") },
    { title: t("contact_step5_t"), desc: t("contact_step5_d") }
  ];

  return (
    <div className="min-h-screen bg-[#0A1F14] text-white selection:bg-[#4CEAAC] selection:text-black flex flex-col font-sans" role="main">
      
      <Navbar activePage="contact" />


      {/* Main Content */}
      <main className="flex-1 pt-32 pb-24 relative overflow-hidden min-h-[calc(100vh-80px)]">
        <HeroBackground />

        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          
          {/* Header Section */}
          <div className="max-w-4xl mb-16 md:mb-24">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-5xl md:text-7xl font-bold font-display tracking-tight leading-[1.1] mb-6"
            >
              {language === 'nl' ? 'Plan direct een ' : 'Book your '}
              <span className="italic text-[#4CEAAC] font-light">{language === 'nl' ? 'kennismaking' : 'intro call'}</span>
              {language === 'nl' ? '.' : '.'}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="text-lg text-white/60 leading-relaxed max-w-2xl"
            >
              {t("contact_desc")}
            </motion.p>
          </div>

          {/* Direct boeken via Cal.com — backlog, flag SHOW_BOOKING */}
          {SHOW_BOOKING && <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-20 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]"
          >
            <div className="flex flex-col gap-6 border-b border-white/10 p-6 md:flex-row md:items-center md:justify-between md:p-8">
              <div className="flex items-center gap-4">
                <img src={robinImg} alt="Robin de Wildt" className="h-14 w-14 rounded-full border-2 border-[#4CEAAC]/30 object-cover object-top" />
                <div>
                  <p className="font-display text-xl font-bold leading-tight">Robin de Wildt</p>
                  <p className="text-sm text-[#4CEAAC]">Digital Growth Expert</p>
                </div>
              </div>
              <div className="flex rounded-full border border-white/10 bg-white/[0.04] p-1 text-sm font-semibold">
                <button
                  onClick={() => setMeetingType('video')}
                  className={`rounded-full px-5 py-2 transition-colors ${meetingType === 'video' ? 'bg-[#4CEAAC] text-[#06140D]' : 'text-white/70 hover:text-white'}`}
                  data-testid="button-meeting-video"
                >
                  {nl ? 'Videocall' : 'Video call'}
                </button>
                <button
                  onClick={() => setMeetingType('office')}
                  className={`rounded-full px-5 py-2 transition-colors ${meetingType === 'office' ? 'bg-[#4CEAAC] text-[#06140D]' : 'text-white/70 hover:text-white'}`}
                  data-testid="button-meeting-office"
                >
                  {nl ? 'Bij ons in Amsterdam' : 'At our office in Amsterdam'}
                </button>
              </div>
            </div>
            <iframe
              key={meetingType}
              src={`https://cal.com/${meetingType === 'video' ? CAL_LINK_VIDEO : CAL_LINK_OFFICE}?embed=true&theme=dark&hide_event_type_details=1`}
              title={nl ? 'Plan een kennismaking' : 'Book an intro call'}
              className="h-[640px] w-full border-0 bg-[#06140D]"
              loading="lazy"
            />
          </motion.div>}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-start">
            
            {/* Left Column: Form & Profile */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="lg:col-span-7 space-y-12"
            >
              {/* Profile Card */}
              <div className="flex flex-col sm:flex-row gap-6 items-center sm:items-start p-6 rounded-2xl bg-white/[0.02] border border-white/5">
                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden shrink-0 border-2 border-[#4CEAAC]/20 relative">
                  <img src={robinImg} alt="Robin de Wildt, Digital Growth Expert bij Wrkt Digital" decoding="async" className="w-full h-full object-cover object-top" />
                </div>
                <div className="text-center sm:text-left pt-2">
                  <h3 className="text-2xl font-bold font-display mb-1">Robin de Wildt</h3>
                  <p className="text-[#4CEAAC] mb-3">Digital Growth Expert</p>
                  <p className="text-sm text-white/60 italic leading-relaxed">
                    {language === 'nl' 
                      ? '"Ik geloof dat groei en merkidentiteit hand in hand gaan. Mijn passie ligt in het creëren van commerciële tractie voor D2C merken - zonder concessies te doen aan wie je bent. Van scroll-stopping creatives tot multi-platform strategieën: samen bouwen we aan duurzame groei die écht bij jouw merk past!"' 
                      : '"I believe growth and brand identity go hand in hand. My passion is creating commercial traction for D2C brands - without compromising who you are. From scroll-stopping creatives to multi-platform strategies: together we build sustainable growth that truly fits your brand!"'}
                  </p>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-8 lg:p-10">
                <h3 className="text-2xl font-bold font-display mb-8">
                  {language === 'nl' ? 'Neem contact op' : 'Get in touch'}
                </h3>

                {isSuccess ? (
                  <div className="h-[400px] flex flex-col items-center justify-center text-center space-y-4">
                    <div className="w-20 h-20 rounded-full bg-[#4CEAAC]/20 flex items-center justify-center mb-4">
                      <Send className="w-8 h-8 text-[#4CEAAC]" />
                    </div>
                    <h3 className="text-3xl font-bold font-display">{t("contact_success_title")}</h3>
                    <p className="text-white/60 text-lg">{t("contact_success_desc")}</p>
                    <Button 
                      variant="outline" 
                      className="mt-8 border-white/20 hover:bg-white/10"
                      onClick={() => {
                        setIsSuccess(false);
                        window.history.replaceState(null, '', localePath('/contact'));
                      }}
                      data-testid="btn-new-request"
                    >
                      {language === 'nl' ? 'Nieuwe aanvraag' : 'New request'}
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-white/80">{t("contact_form_name")} *</label>
                        <Input 
                          required
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          className="bg-[#0A0A0A] border-white/10 focus:border-[#4CEAAC] focus:ring-1 focus:ring-[#4CEAAC] text-white h-12" 
                          data-testid="input-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-white/80">{t("contact_form_email")} *</label>
                        <Input 
                          required 
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                          className="bg-[#0A0A0A] border-white/10 focus:border-[#4CEAAC] focus:ring-1 focus:ring-[#4CEAAC] text-white h-12" 
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-white/80">{t("contact_form_company")} *</label>
                      <Input 
                        required
                        value={formData.company}
                        onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                        className="bg-[#0A0A0A] border-white/10 focus:border-[#4CEAAC] focus:ring-1 focus:ring-[#4CEAAC] text-white h-12" 
                        data-testid="input-company"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-white/80">{t("contact_form_budget")} *</label>
                      <Select required value={formData.source} onValueChange={(val) => setFormData(prev => ({ ...prev, source: val }))}>
                        <SelectTrigger className="bg-[#0A0A0A] border-white/10 focus:border-[#4CEAAC] focus:ring-1 focus:ring-[#4CEAAC] text-white h-12" data-testid="select-source">
                          <SelectValue placeholder={language === 'nl' ? 'Selecteer...' : 'Select...'} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-white/10 text-white">
                          <SelectItem value="linkedin">LinkedIn</SelectItem>
                          <SelectItem value="google">Google</SelectItem>
                          <SelectItem value="ai">{language === 'nl' ? 'AI (ChatGPT, Claude, etc.)' : 'AI (ChatGPT, Claude, etc.)'}</SelectItem>
                          <SelectItem value="referral">{language === 'nl' ? 'Aanbeveling / Mond-tot-mond' : 'Referral'}</SelectItem>
                          <SelectItem value="other">{language === 'nl' ? 'Anders' : 'Other'}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-white/80">{t("contact_form_message")}</label>
                      <Textarea 
                        value={formData.message}
                        onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                        className="bg-[#0A0A0A] border-white/10 focus:border-[#4CEAAC] focus:ring-1 focus:ring-[#4CEAAC] text-white min-h-[120px] resize-y" 
                        data-testid="input-message"
                      />
                    </div>

                    <div className="pt-2 text-xs text-white/40 leading-relaxed mb-6">
                      {language === 'nl' 
                        ? 'Door op \'Verstuur Aanvraag\' te klikken ga je akkoord met onze privacy policy en voorwaarden.' 
                        : 'By clicking \'Send Request\' you confirm that you agree with our privacy policy and terms.'}
                    </div>

                    <Button 
                      type="submit" 
                      disabled={mutation.isPending}
                      className="btn btn--mint btn--lg w-full"
                      data-testid="btn-submit"
                    >
                      {mutation.isPending ? (
                        <span className="flex items-center gap-2">
                          <span className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin"></span>
                          {language === 'nl' ? 'Verzenden...' : 'Sending...'}
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          {t("contact_form_submit")}
                          <ArrowLeft className="w-5 h-5 rotate-135" />
                        </span>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </motion.div>

            {/* Right Column: Steps */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="lg:col-span-5"
            >
              <div className="sticky top-32 z-10 bg-[#0A1F14] p-6 md:p-8 rounded-2xl border border-white/5">
                <h3 className="text-2xl md:text-3xl font-bold font-display mb-10">
                  {language === 'nl' ? 'Wat je kunt verwachten' : 'Here\'s what to expect'}
                </h3>
                
                <div className="space-y-0 relative ml-6">
                  <div className="absolute left-[19px] top-6 bottom-6 w-px bg-gradient-to-b from-[#4CEAAC]/40 via-[#4CEAAC]/20 to-transparent" />
                  {steps.map((step, index) => (
                    <div key={index} className="relative flex items-start gap-8 pb-10 last:pb-0">
                      <div className="w-10 h-10 rounded-full bg-[#0A1F14] border-2 border-[#4CEAAC] text-[#4CEAAC] flex items-center justify-center font-bold shrink-0 z-10 text-sm shadow-[0_0_12px_rgba(76,234,172,0.2)]">
                        0{index + 1}
                      </div>
                      <div className="pt-1.5">
                        <h4 className="text-lg font-bold font-display mb-2">{step.title.split('. ')[1]}</h4>
                        <p className="text-white/60 leading-relaxed text-[15px]">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-12 pt-8 border-t border-white/10 space-y-6">
                  <div className="flex items-start gap-5">
                    <div className="w-11 h-11 rounded-full bg-[#4CEAAC]/10 border border-[#4CEAAC]/20 flex items-center justify-center shrink-0">
                      <Mail className="w-5 h-5 text-[#4CEAAC]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-white/50 mb-1">E-mail</h4>
                      <p className="text-white">
                        <a href={`mailto:${t("contact_email")}`} className="hover:text-[#4CEAAC] transition-colors" data-testid="link-email">
                          {t("contact_email")}
                        </a>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-5">
                    <div className="w-11 h-11 rounded-full bg-[#4CEAAC]/10 border border-[#4CEAAC]/20 flex items-center justify-center shrink-0">
                      <MapPin className="w-5 h-5 text-[#4CEAAC]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-white/50 mb-1">{language === 'nl' ? 'Kantoor' : 'Office'}</h4>
                      <p className="text-white">{t("contact_address")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}